import StudentInputSettings from "../../../shared-components/StudentInputSettings";
import React, { useEffect, useState, useMemo } from "react";
import {
  Stack,
  Typography,
  Card,
  CardContent,
  Switch,
  Button,
  Divider,
} from "@mui/material";
import { useApiClient } from "../../../api";
import SaveIcon from "@mui/icons-material/Save";
import GeneralLoading from "../../../shared-components/Loading";
import { enqueueSnackbar } from "notistack";
import { DEFAULT_PREFS, MARKING_MODES } from "../../../utils/constants";
import { isHolisticMarking } from "../../../utils/preferences";
import { checkHasApprovedSubscription } from "../../auth/Utils";

import { SignupPurposePreferences } from "./SignupPurposePreferences";
import { useAuthenticationStore } from "../../../stores/auth";

export const UserPreferences = () => {
  const ApiClient = useApiClient();
  const { user } = useAuthenticationStore();
  const hasApprovedSubscription = checkHasApprovedSubscription();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [serverPrefs, setServerPrefs] = useState(DEFAULT_PREFS);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useEffect(() => {
    const fetchExistingData = async () => {
      try {
        const response = await ApiClient.users.getUserPreferences();
        if (response?.data) {
          const parsed = JSON.parse(response.data);

          const sanitized = {
            allow_spelling_assistance:
              parsed.allow_spelling_assistance !== false,
            allow_copy_paste: parsed.allow_copy_paste !== false,
            hide_marks: Boolean(parsed.hide_marks), // Explicit cast
            marking_mode: parsed.marking_mode || MARKING_MODES.ANALYTIC,
            auto_release_marks: Boolean(parsed.auto_release_marks),
            notify_students_by_email_of_feedback: Boolean(
              parsed.notify_students_by_email_of_feedback,
            ),
          };

          setPrefs(sanitized);
          setServerPrefs(sanitized);
        }
      } catch (err) {
        console.error("Failed to load preferences: ", err);
      } finally {
        setLoading(false);
      }
    };

    fetchExistingData();
  }, []);

  const handleChange = (key, value) => {
    setPrefs((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const isDirty = useMemo(() => {
    return JSON.stringify(prefs) !== JSON.stringify(serverPrefs);
  }, [prefs, serverPrefs]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // In your actual implementation, this sends the object to your Django JSONField
      const response = await ApiClient.users.updateUserPreferences(prefs);
      if (response.success) {
        setServerPrefs(prefs);
        enqueueSnackbar("Preferences updated", {
          variant: "success",
          autoHideDuration: 3000,
        });
      } else {
        enqueueSnackbar("There was a problem saving your preferences", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (err) {
      enqueueSnackbar("There was a problem saving your preferences", {
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const enableHideMarks = false; // TODO: future - out of scope for this ticket but leaving now for future

  if (loading) return <GeneralLoading />;

  return (
    <Card variant="shadow">
      <CardContent>
        <Typography variant="h3" sx={{ mb: 3 }}>
          Preferences
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Note: these will only change for assignments, answers, etc. you create
          from this point onwards. Existing examples will need to be changed
          manually.
        </Typography>

        <StudentInputSettings
          prefs={prefs}
          onChange={handleChange}
          disabled={saving}
        />
        <Stack
          spacing={4}
          divider={<Divider orientation="horizontal" flexItem />}
        >
          {/* Marking Mode */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack spacing={0.5} sx={{ pr: 4 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Holistic Marking
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use Holistic marking for future assignments.
              </Typography>
            </Stack>
            <Switch
              checked={isHolisticMarking(prefs)}
              onChange={(e) =>
                handleChange(
                  "marking_mode",
                  e.target.checked
                    ? MARKING_MODES.HOLISTIC
                    : MARKING_MODES.ANALYTIC,
                )
              }
            />
          </Stack>
          {hasApprovedSubscription && (
            <>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack spacing={0.5} sx={{ pr: 4 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Auto Release Marks
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Allow students to see their marks as soon as Fruitify has
                    marked them
                  </Typography>
                </Stack>
                <Switch
                  checked={prefs.auto_release_marks}
                  onChange={(e) =>
                    handleChange("auto_release_marks", e.target.checked)
                  }
                />
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack spacing={0.5} sx={{ pr: 4 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Notify students of feedback
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Send an email to students notifying them their feedback is
                    ready
                  </Typography>
                </Stack>
                <Switch
                  checked={prefs.notify_students_by_email_of_feedback}
                  onChange={(e) =>
                    handleChange(
                      "notify_students_by_email_of_feedback",
                      e.target.checked,
                    )
                  }
                />
              </Stack>
              {enableHideMarks && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack spacing={0.5} sx={{ pr: 4 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Hide Marks
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Hide numerical scores from students across all
                      assignments.
                    </Typography>
                  </Stack>
                  <Switch
                    checked={prefs.hide_marks}
                    onChange={(e) =>
                      handleChange("hide_marks", e.target.checked)
                    }
                  />
                </Stack>
              )}
            </>
          )}
          {/* Save Action */}
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              startIcon={
                saving ? (
                  <GeneralLoading size={20} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              onClick={handleSave}
              disabled={!isDirty || saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </Stack>
        </Stack>
        {user?.info?.activeContext?.roleAssignment?.role?.code === "teacher" &&
          !user?.beingImpersonated && (
            <SignupPurposePreferences key={user.info.id} user={user} />
          )}
      </CardContent>
    </Card>
  );
};
